import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { xpApi, participantAuth } from "@/lib/experienceApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wine, Trophy, Users, QrCode, LogOut, User, Star, FileDown, ShoppingBag, Sparkles, Share2, Plus, Minus, Heart, StickyNote, Mic, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

export default function ExperienceMy() {
  const [me, setMe] = useState(null);
  const [wines, setWines] = useState([]);
  const [tab, setTab] = useState("wines");
  const [teams, setTeams] = useState([]);
  const [lbInd, setLbInd] = useState([]);
  const [lbTeams, setLbTeams] = useState([]);
  const [addons, setAddons] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [m, w] = await Promise.all([xpApi.me(), xpApi.listWines()]);
      setMe(m); setWines(w);
    } catch (e) {
      toast.error("Session expirée");
      participantAuth.clear();
      nav("/experience");
    }
  };

  useEffect(() => {
    if (!participantAuth.getToken()) { nav("/experience"); return; }
    load();
  }, [nav]);

  useEffect(() => {
    if (tab === "teams") xpApi.listTeams().then(setTeams).catch(() => {});
    if (tab === "leaderboard") {
      xpApi.leaderboardIndividual().then(setLbInd).catch(() => {});
      xpApi.leaderboardTeams().then(setLbTeams).catch(() => {});
    }
    if (tab === "shop") xpApi.listAddons().then(setAddons).catch(() => setAddons({ bottles: [], merch: [] }));
  }, [tab]);

  const logout = () => { participantAuth.clear(); nav("/experience"); };

  if (!me) return <div className="p-6 text-center text-sm text-neutral-500">Chargement…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="rounded-2xl p-5 mb-4 text-white"
        style={{ background: `linear-gradient(135deg, ${me.event?.branding?.primary_color || '#E5276A'} 0%, #1F3A5F 100%)` }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-70">{me.event?.name}</div>
            <div className="mt-1 font-mono text-lg">{me.ticket.participant_code}</div>
            <div className="text-xs mt-2 opacity-90 flex items-center gap-3">
              <span className="flex items-center gap-1"><Wine className="w-3.5 h-3.5" /> {me.ticket.tastings_remaining} restantes</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" /> {me.score} pts</span>
              {me.team && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {me.team.name}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <a href={`${API}/experience/tickets/${me.ticket.id}/pdf`} target="_blank" rel="noreferrer"
               className="p-2 rounded-lg hover:bg-white/10" title="Télécharger PDF">
              <FileDown className="w-4 h-4" />
            </a>
            <button onClick={logout} data-testid="logout-btn" className="p-2 rounded-lg hover:bg-white/10">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-4 bg-neutral-100 rounded-xl p-1 overflow-x-auto">
        {[
          { k: "wines", label: "Vins", icon: Wine },
          { k: "shop", label: "Boutique", icon: ShoppingBag },
          { k: "souvenir", label: "Souvenir", icon: Sparkles },
          { k: "teams", label: "Équipes", icon: Users },
          { k: "leaderboard", label: "Classement", icon: Trophy },
          { k: "account", label: "Compte", icon: User },
        ].map((t) => {
          const Ic = t.icon;
          return (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1 transition whitespace-nowrap ${tab === t.k ? 'bg-white shadow text-[#E5276A]' : 'text-neutral-600'}`}>
              <Ic className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "wines" && <WinesTab wines={wines} refresh={load} disabled={me.ticket.tastings_remaining <= 0} />}
      {tab === "shop" && <ShopTab addons={addons} refresh={() => xpApi.listAddons().then(setAddons)} />}
      {tab === "souvenir" && <SouvenirTab me={me} />}
      {tab === "teams" && <TeamsTab me={me} teams={teams} refresh={() => { load(); xpApi.listTeams().then(setTeams); }} />}
      {tab === "leaderboard" && <LeaderboardTab me={me} lbInd={lbInd} lbTeams={lbTeams} />}
      {tab === "account" && <AccountTab me={me} refresh={load} />}
    </div>
  );
}

function WinesTab({ wines, refresh, disabled }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);
  const [favBusy, setFavBusy] = useState(null);
  const [podiumFor, setPodiumFor] = useState(null); // wine object after fresh fav
  const [notesFor, setNotesFor] = useState(null); // wine object for notes modal

  const requestToken = async (wineId) => {
    if (disabled) { toast.error("Plus de dégustations disponibles"); return; }
    setBusy(wineId);
    try {
      const res = await xpApi.requestTastingToken(wineId);
      sessionStorage.setItem("gv_last_qr", JSON.stringify(res));
      nav(`/experience/tasting/${wineId}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible");
    } finally { setBusy(null); }
  };

  const toggleFav = async (wine) => {
    setFavBusy(wine.id);
    try {
      const r = await xpApi.toggleFavorite(wine.id);
      toast.success(r.favorite ? `Coup de cœur ! +${r.points_awarded} pts` : "Retiré");
      if (r.favorite) setPodiumFor(wine);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
    finally { setFavBusy(null); }
  };

  return (
    <div className="grid gap-2">
      {wines.length === 0 && <p className="text-sm text-neutral-500 text-center py-8">Aucun vin à afficher</p>}
      {wines.map(w => {
        const soldOut = w.inventory?.status === "sold_out" || w.inventory?.doses_remaining <= 0;
        const tasted = !!w.tasting?.served_at;
        const isFav = !!w.tasting?.favorite;
        const hasNotes = !!w.tasting?.notes;
        return (
          <Card key={w.id} data-testid={`wine-${w.id}`} className={soldOut ? "opacity-50" : ""}>
            <CardContent className="p-3 flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-[#E5276A]/10 flex items-center justify-center flex-shrink-0">
                <Wine className="w-5 h-5 text-[#E5276A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{w.wine_ref?.name}</div>
                <div className="text-xs text-neutral-500">{w.wine_ref?.type} · {w.inventory?.doses_remaining ?? 0} doses</div>
                {hasNotes && w.tasting?.notes?.score != null && (
                  <div className="text-[10px] text-[#E5276A] mt-0.5">
                    Ma note : <b>{w.tasting.notes.score}/100</b>
                  </div>
                )}
              </div>
              {tasted && (
                <>
                  <button onClick={() => setNotesFor(w)} data-testid={`notes-${w.id}`}
                          title="Notes de dégustation"
                          className={`p-1.5 rounded-full transition ${hasNotes ? "text-[#E5276A]" : "text-neutral-300 hover:text-neutral-500"}`}>
                    <StickyNote className={`w-5 h-5 ${hasNotes ? "fill-[#E5276A]/20" : ""}`} />
                  </button>
                  <button onClick={() => toggleFav(w)} disabled={favBusy === w.id}
                          data-testid={`fav-${w.id}`}
                          title={isFav ? "Retirer coup de cœur" : "Coup de cœur"}
                          className={`p-1.5 rounded-full transition ${isFav ? "text-[#E5276A]" : "text-neutral-300 hover:text-neutral-500"}`}>
                    <Heart className={`w-5 h-5 ${isFav ? "fill-[#E5276A]" : ""}`} />
                  </button>
                </>
              )}
              {tasted ? (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">Dégusté</span>
              ) : (
                <Button size="sm" onClick={() => requestToken(w.id)} disabled={busy === w.id || soldOut || disabled}
                  className="bg-[#E5276A] hover:bg-[#C01661]">
                  <QrCode className="w-3.5 h-3.5 mr-1" /> {busy === w.id ? "…" : "Choisir"}
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
      {podiumFor && <PodiumShareModal wine={podiumFor} onClose={() => setPodiumFor(null)} />}
      {notesFor && <NotesModal wine={notesFor} onClose={() => setNotesFor(null)} onSaved={refresh} />}
    </div>
  );
}

function NotesModal({ wine, onClose, onSaved }) {
  const initial = wine.tasting?.notes || {};
  const [f, setF] = useState({
    score: initial.score ?? "",
    robe: initial.robe || "",
    nez: initial.nez || "",
    bouche: initial.bouche || "",
    comment: initial.comment || "",
  });
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = React.useRef(null);
  const chunksRef = React.useRef([]);

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Micro non disponible");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                  : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setTranscribing(true);
        try {
          const r = await xpApi.transcribeNotes(wine.id, blob);
          const ex = r.extracted || {};
          setF(prev => ({
            ...prev,
            robe: ex.robe || prev.robe,
            nez: ex.nez || prev.nez,
            bouche: ex.bouche || prev.bouche,
            comment: ex.comment || r.text || prev.comment,
          }));
          toast.success("Notes transcrites — relisez et ajustez si besoin");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription impossible");
        } finally { setTranscribing(false); }
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Permission micro refusée");
    }
  };
  const stopRec = () => {
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...(f.score !== "" && !isNaN(parseFloat(f.score)) ? { score: parseFloat(f.score) } : {}),
        ...(f.robe ? { robe: f.robe } : {}),
        ...(f.nez ? { nez: f.nez } : {}),
        ...(f.bouche ? { bouche: f.bouche } : {}),
        ...(f.comment ? { comment: f.comment } : {}),
      };
      if (Object.keys(payload).length === 0) { toast.error("Rien à enregistrer"); return; }
      const r = await xpApi.saveTastingNotes(wine.id, payload);
      toast.success(r.carnet_id ? "Enregistré dans mon Carnet" : "Notes enregistrées");
      onSaved(); onClose();
    } catch { toast.error("Impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4 overlay-safe pb-[calc(1rem_+_var(--safe-bottom))]"
         onClick={onClose} data-testid="notes-modal">
      <div className="bg-white rounded-2xl max-w-md w-full p-4 shadow-xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold flex items-center gap-1">
              <StickyNote className="w-4 h-4" /> Notes de dégustation
            </div>
            <div className="text-lg font-bold text-[#1F3A5F] mt-1">{wine.wine_ref?.name}</div>
            <p className="text-xs text-neutral-500 mt-1">
              Ces notes seront sauvegardées dans ton Carnet personnel.
            </p>
          </div>
          <button
            data-testid={recording ? "stop-mic" : "start-mic"}
            onClick={recording ? stopRec : startRec}
            disabled={transcribing}
            title={recording ? "Arrêter" : "Dicter mes notes"}
            className={`p-3 rounded-full transition ${recording ? "bg-red-500 text-white animate-pulse" : transcribing ? "bg-neutral-100 text-neutral-400" : "bg-[#E5276A]/10 text-[#E5276A] hover:bg-[#E5276A]/20"}`}>
            {transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mic className="w-5 h-5" />}
          </button>
        </div>
        {(recording || transcribing) && (
          <div className={`text-xs mb-2 text-center rounded-md py-1 ${recording ? "bg-red-50 text-red-600" : "bg-neutral-50 text-neutral-500"}`}>
            {recording ? "🎙 Enregistrement… Cliquez sur STOP quand vous avez fini" : "Transcription en cours…"}
          </div>
        )}
        <div className="grid gap-2">
          <div>
            <label className="text-xs text-neutral-600">Note /100</label>
            <Input type="number" min="0" max="100" step="0.5" value={f.score}
                   onChange={(e) => setF({...f, score: e.target.value})}
                   data-testid="notes-score" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">🍷 Robe</label>
            <Input value={f.robe} onChange={(e) => setF({...f, robe: e.target.value})}
                   placeholder="Rubis, tuilé, or…" data-testid="notes-robe" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">👃 Nez</label>
            <Input value={f.nez} onChange={(e) => setF({...f, nez: e.target.value})}
                   placeholder="Fruits rouges, épices…" data-testid="notes-nez" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">👄 Bouche</label>
            <Input value={f.bouche} onChange={(e) => setF({...f, bouche: e.target.value})}
                   placeholder="Structure, tanins…" data-testid="notes-bouche" />
          </div>
          <div>
            <label className="text-xs text-neutral-600">Commentaire libre</label>
            <textarea value={f.comment} onChange={(e) => setF({...f, comment: e.target.value})}
                      placeholder="Impressions générales, accords…"
                      className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px]"
                      data-testid="notes-comment" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="save-notes"
                  className="flex-1 bg-[#E5276A] hover:bg-[#C01661]">
            {busy ? "…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PodiumShareModal({ wine, onClose }) {
  const token = participantAuth.getToken();
  const url = `${API}/experience/me/podium/${wine.id}.png`;
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const file = new File([blob], `podium-${wine.id.slice(0,6)}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Mon coup de cœur",
          text: `Je vote pour ${wine.wine_ref?.name} !`,
        });
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `podium-${wine.id.slice(0,6)}.png`;
        link.click();
        toast.success("Visuel téléchargé");
      }
    } catch { toast.error("Impossible"); }
    finally { setBusy(false); onClose(); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4 overlay-safe pb-[calc(1rem_+_var(--safe-bottom))]"
         onClick={onClose} data-testid="podium-modal">
      <div className="bg-white rounded-2xl max-w-sm w-full p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-3">
          <div className="text-xs uppercase tracking-widest text-[#E5276A] font-semibold mb-1">
            <Heart className="w-4 h-4 inline fill-[#E5276A]" /> Coup de cœur enregistré
          </div>
          <p className="text-sm text-neutral-600">Partagez votre vote sur Instagram avec un visuel personnalisé.</p>
        </div>
        <AuthedImage url={url} className="w-full rounded-xl mb-3" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">Plus tard</Button>
          <Button onClick={share} disabled={busy} data-testid="podium-share"
                  className="flex-1 bg-[#E5276A] hover:bg-[#C01661]">
            <Share2 className="w-4 h-4 mr-1" /> {busy ? "…" : "Partager"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TeamsTab({ me, teams, refresh }) {
  const [newName, setNewName] = useState("");
  const create = async () => {
    if (!newName.trim()) return;
    try { await xpApi.createTeam({ name: newName.trim() }); toast.success("Équipe créée !"); setNewName(""); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  const join = async (id) => {
    try { await xpApi.joinTeam({ team_id: id }); toast.success("Rejoint !"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };

  if (me.team) {
    return (
      <Card><CardContent className="p-4">
        <div className="text-xs text-neutral-500">Votre équipe</div>
        <div className="text-lg font-semibold mt-1">{me.team.name}</div>
        <div className="text-xs mt-2">Points cumulés : <b>{me.team.total_points_cache || 0}</b></div>
      </CardContent></Card>
    );
  }
  return (
    <div>
      <Card className="mb-3"><CardContent className="p-3">
        <div className="text-xs uppercase text-neutral-500 mb-2">Créer une équipe</div>
        <div className="flex gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Nom de l'équipe" className="flex-1 border rounded-md px-3 py-1.5 text-sm" />
          <Button size="sm" onClick={create} className="bg-[#E5276A] hover:bg-[#C01661]">Créer</Button>
        </div>
      </CardContent></Card>
      <div className="grid gap-2">
        {teams.map(t => (
          <Card key={t.id}><CardContent className="p-3 flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">{t.name}</div>
              <div className="text-xs text-neutral-500">{t.total_points_cache || 0} pts</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => join(t.id)}>Rejoindre</Button>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

function LeaderboardTab({ me, lbInd, lbTeams }) {
  return (
    <div className="grid gap-4">
      <Card><CardContent className="p-3">
        <div className="text-xs uppercase text-neutral-500 mb-2">Individuel</div>
        {lbInd.map((t, i) => (
          <div key={t.id} className={`flex items-center gap-2 py-1 border-b last:border-b-0 ${t.id === me.ticket.id ? "font-semibold text-[#E5276A]" : ""}`}>
            <span className="w-6 text-center text-sm">{i + 1}</span>
            <span className="flex-1 font-mono text-xs">{t.participant_code}</span>
            <span className="text-sm font-bold">{t.points_cache || 0}</span>
          </div>
        ))}
      </CardContent></Card>
      <Card><CardContent className="p-3">
        <div className="text-xs uppercase text-neutral-500 mb-2">Équipes</div>
        {lbTeams.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2 py-1 border-b last:border-b-0">
            <span className="w-6 text-center text-sm">{i + 1}</span>
            <span className="flex-1 text-sm">{t.name}</span>
            <span className="text-sm font-bold">{t.total_points_cache || 0}</span>
          </div>
        ))}
      </CardContent></Card>
    </div>
  );
}


/* ────────── Shop tab (bottles + merch) ────────── */
function ShopTab({ addons, refresh }) {
  const [cart, setCart] = useState({}); // key -> { kind, ref_id, quantity, name, price }
  const [busy, setBusy] = useState(false);

  if (!addons) return <p className="text-sm text-neutral-500 text-center py-6">Chargement…</p>;
  const bottles = addons.bottles || [];
  const merch = addons.merch || [];
  const isEmpty = bottles.length === 0 && merch.length === 0;

  const inc = (key, item) => {
    const c = { ...cart };
    if (!c[key]) c[key] = { ...item, quantity: 0 };
    c[key].quantity = Math.min((c[key].quantity || 0) + 1, item.max);
    setCart(c);
  };
  const dec = (key) => {
    const c = { ...cart };
    if (!c[key]) return;
    c[key].quantity = Math.max(0, c[key].quantity - 1);
    if (c[key].quantity === 0) delete c[key];
    setCart(c);
  };
  const total = Object.values(cart).reduce((s, l) => s + l.price * l.quantity, 0);
  const nbItems = Object.values(cart).reduce((s, l) => s + l.quantity, 0);

  const checkout = async () => {
    const items = Object.values(cart).filter(l => l.quantity > 0).map(l => ({
      kind: l.kind, ref_id: l.ref_id, quantity: l.quantity,
    }));
    if (items.length === 0) { toast.error("Panier vide"); return; }
    setBusy(true);
    try {
      const r = await xpApi.addonCheckout({ items, origin_url: window.location.origin });
      window.location.href = r.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible");
    } finally { setBusy(false); }
  };

  if (isEmpty) {
    return (
      <Card><CardContent className="p-6 text-center">
        <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
        <p className="text-sm text-neutral-500">
          Dégustez des vins pour débloquer l'achat des bouteilles, ou revenez plus tard : de nouveaux articles peuvent apparaître.
        </p>
      </CardContent></Card>
    );
  }

  return (
    <div className="pb-24">
      {bottles.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-[#1F3A5F] mb-2 flex items-center gap-1">
            <Wine className="w-4 h-4 text-[#E5276A]" /> Repartez avec les bouteilles dégustées
          </h3>
          <div className="grid gap-2 mb-4">
            {bottles.map(b => {
              const key = `bottle:${b.id}`;
              const item = {
                kind: "bottle", ref_id: b.id, name: b.wine_ref?.name,
                price: b.bottle_purchase_price_cents, max: b.bottle_purchase_stock,
              };
              const q = cart[key]?.quantity || 0;
              return (
                <Card key={b.id} data-testid={`shop-bottle-${b.id}`}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-sm">{b.wine_ref?.name}</div>
                      <div className="text-xs text-neutral-500">Stock : {b.bottle_purchase_stock}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#E5276A]">{(b.bottle_purchase_price_cents/100).toFixed(2)}€</span>
                      <QtyControl q={q} onInc={() => inc(key, item)} onDec={() => dec(key)} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
      {merch.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-[#1F3A5F] mb-2 flex items-center gap-1">
            <ShoppingBag className="w-4 h-4 text-[#E5276A]" /> Objets & coffrets
          </h3>
          <div className="grid gap-2">
            {merch.map(m => {
              const key = `merch:${m.id}`;
              const item = {
                kind: "merch", ref_id: m.id, name: m.name,
                price: m.price_cents, max: m.stock,
              };
              const q = cart[key]?.quantity || 0;
              return (
                <Card key={m.id} data-testid={`shop-merch-${m.id}`}>
                  <CardContent className="p-3 flex items-center gap-3">
                    {m.image_url && (
                      <img src={m.image_url} alt={m.name}
                           className="w-14 h-14 rounded-md object-cover bg-neutral-100" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{m.name}</div>
                      {m.description && <div className="text-xs text-neutral-500 truncate">{m.description}</div>}
                      <div className="text-[10px] text-neutral-400">Stock : {m.stock}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#E5276A] text-sm">{(m.price_cents/100).toFixed(2)}€</span>
                      <QtyControl q={q} onInc={() => inc(key, item)} onDec={() => dec(key)} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
      {nbItems > 0 && (
        <div className="fixed bottom-[calc(var(--m-tabs-h)_+_1rem)] md:bottom-4 left-4 right-4 max-w-3xl mx-auto z-30">
          <Card className="border-2 border-[#E5276A] shadow-lg">
            <CardContent className="p-3 flex items-center gap-3">
              <div className="flex-1">
                <div className="text-xs text-neutral-500">
                  {nbItems} article{nbItems>1?"s":""} · livraison Stripe
                </div>
                <div className="text-lg font-bold text-[#E5276A]">{(total/100).toFixed(2)} €</div>
              </div>
              <Button onClick={checkout} disabled={busy} data-testid="addon-checkout"
                      className="bg-[#E5276A] hover:bg-[#C01661]">
                {busy ? "…" : "Payer"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function QtyControl({ q, onInc, onDec }) {
  if (q === 0) return (
    <Button size="sm" variant="outline" onClick={onInc} className="h-7 px-2 text-xs">
      <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
    </Button>
  );
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={onDec} className="h-6 w-6 p-0"><Minus className="w-3 h-3" /></Button>
      <span className="text-sm font-bold w-4 text-center">{q}</span>
      <Button size="sm" variant="outline" onClick={onInc} className="h-6 w-6 p-0"><Plus className="w-3 h-3" /></Button>
    </div>
  );
}

/* ────────── Souvenir tab ────────── */
function SouvenirTab({ me }) {
  const token = participantAuth.getToken();
  const url = `${API}/experience/me/souvenir.png?t=${token?.slice(0, 10)}`;
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true);
    try {
      const r = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const file = new File([blob], "geovinum-souvenir.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Mon carnet · ${me.event?.name}`,
          text: `J'ai dégusté ${(me.wines_tasted_count || 0)} vins à ${me.event?.name} !`,
        });
      } else {
        // Fallback : direct download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "geovinum-souvenir.png";
        link.click();
        toast.success("Souvenir téléchargé");
      }
    } catch { toast.error("Impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <p className="text-xs text-neutral-500 mb-3 text-center">
        Votre carte souvenir Instagram · Points, vins dégustés & QR de l'événement
      </p>
      <AuthedImage url={url} className="w-full rounded-2xl shadow-lg mb-4" />
      <Button onClick={share} disabled={busy} data-testid="souvenir-share"
              className="w-full bg-[#E5276A] hover:bg-[#C01661]">
        <Share2 className="w-4 h-4 mr-2" /> {busy ? "…" : "Partager / Enregistrer"}
      </Button>
    </div>
  );
}

function AuthedImage({ url, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const token = participantAuth.getToken();
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => setSrc(URL.createObjectURL(b)))
      .catch(() => {});
  }, [url]);
  if (!src) return <div className={`${className} bg-neutral-100 aspect-[4/5] flex items-center justify-center text-xs text-neutral-500`}>Génération…</div>;
  return <img src={src} alt="Souvenir" className={className} data-testid="souvenir-image" />;
}

function AccountTab({ me, refresh }) {
  const [claim, setClaim] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await xpApi.participantClaim(claim);
      toast.success("Compte lié ! Vos dégustations seront sauvegardées.");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
    finally { setBusy(false); }
  };
  if (me.ticket.user_id) {
    return <Card><CardContent className="p-4 text-sm">Votre billet est lié à un compte geoVINUM. Vos dégustations seront sauvegardées dans votre Carnet.</CardContent></Card>;
  }
  return (
    <Card><CardContent className="p-4 space-y-2">
      <div className="text-sm text-neutral-700 mb-2">
        Créez un compte gratuit pour retrouver vos vins dégustés dans votre <b>Carnet geoVINUM</b> après l'événement.
      </div>
      <input placeholder="Nom" value={claim.name} onChange={(e) => setClaim({ ...claim, name: e.target.value })} className="w-full border rounded-md px-3 py-1.5 text-sm" />
      <input placeholder="Email" type="email" value={claim.email} onChange={(e) => setClaim({ ...claim, email: e.target.value })} className="w-full border rounded-md px-3 py-1.5 text-sm" />
      <input placeholder="Mot de passe" type="password" value={claim.password} onChange={(e) => setClaim({ ...claim, password: e.target.value })} className="w-full border rounded-md px-3 py-1.5 text-sm" />
      <Button onClick={submit} disabled={busy} className="w-full bg-[#E5276A] hover:bg-[#C01661]">
        {busy ? "…" : "Créer et lier"}
      </Button>
    </CardContent></Card>
  );
}
